package net.sergeych.kilogin

import net.sergeych.bintools.ByteChunk
import net.sergeych.bintools.asChunk
import net.sergeych.crypto2.KDF
import net.sergeych.crypto2.SymmetricKey

/**
 * RestoreKey is a specially constructed highly random string that produces cryptographically
 * independent ID and symmetric key tohether with some tolerance to human typing errors and minimal
 * integrity check to simplify its entry.
 *
 * The restore key is too long to remember, it should better be copied to some safe place.It is intended
 * as a last resort to restore access to something impprtant using [AccessControlObject] object.
 *
 * Construct new instance with [generate], check with [checkSecretIntegrity] and derive key and id
 * with [parse]
 */
class RestoreKey private constructor(
    val restoreId: ByteChunk,
    val key: SymmetricKey,
    val secret: String,
) {

    class InvalidSecretException : Exception("secret is not valid")

    companion object {

        /**
         * Remove auxiliary characters from secret
         */
        fun p(secret: String): String = secret.trim().replace("-", "")

        /**
         * Perform internal integrity check against occasional mistypes. This check does
         * not mean it the key are ok to decrypt anything, just that the secret was not mistyped.
         *
         * This method does not alter key strength which is 136 random bits.
         */
        fun checkSecretIntegrity(secret: String): Boolean {
            return kotlin.runCatching { Safe58.decode(p(secret)) }.isSuccess
        }

        /**
         * Parse the secret (secret key combination) and provide corresponding id and key to
         * find and decrypt [AccessControlObject] object.
         * @return restoreId to restoreKey pair
         * @throws Safe58.InvalidCrcException if the secret is not valid, see [checkSecretIntegrity].
         */
        fun parse(secret: String, complexity: KDF.Complexity): Pair<ByteChunk, SymmetricKey> {
            // Ensure the secret is valid
            val s = p(secret)
            if (!checkSecretIntegrity(s)) throw InvalidSecretException()

            val kdf = KDF.Argon.create(complexity,
                    KDF.Argon.deriveSaltFromString("KiLogin restore access key"),
                2*SymmetricKey.keyLength)

            return kdf.deriveMultipleKeys(s, 2).let { (a, b) -> a.keyBytes.asChunk() to b }
        }

        /**
         * Generate brand new random secret and return it and parsed id and key.
         */
        fun generate(complexity: KDF.Complexity): RestoreKey {
            // THIS IS IMPORTANT not to use crc-protected safe58
            // because in the case somebody has seen _part_ of the secret with crc (last chars)
            // it will simplify brute force the rest, so, no CRC.
            // 18 bytes give 5 groups by 5 characters, which is better
            // for humans:
            val secret = Safe58.encode(randomBytes(18))
            val (id, key) = parse(secret,complexity)
            val (id1, key1) = parse(secret,complexity)
            require(id1 == id)
            require(key == key1)
            return RestoreKey(id, key, secret.chunked(5).joinToString("-"))
        }
    }
}